/**
 * /sitemap — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * ── Only routes that exist ────────────────────────────────────────────────
 * The pre-Bloom version listed /platform/cloud-servers and four individual
 * /blog/* posts that do not exist in this app — every one a button that
 * navigated to a 404. Those entries are still absent for that reason.
 *
 * The use cases and the blog index ARE here, because they were built.
 *
 * A sitemap is a claim about what a site contains. One that lists pages which
 * are not there is the same failure as a log line naming a route that does not
 * exist — confidently wrong, and worse than saying less. Those entries are gone
 * rather than carried across; add them back when the pages land.
 *
 * The XML section is honest for the same reason: public/sitemap.xml is a real
 * file. It is hand-maintained — if you add a public route, add it there too.
 */
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/shared/SEO';
import { ArrowRight, ChevronRight, Home, LogIn, UserPlus, BookOpen, Mail, Handshake, FileText, Shield, Cookie, Activity, Map, Server, RotateCcw, XCircle, Briefcase, Building2, ShoppingCart, Truck, HeartPulse, Newspaper } from 'lucide-react';

interface SitemapLink {
  label: string;
  href: string;
  desc?: string;
  icon?: ComponentType<{ className?: string }>;
}

interface SitemapSection {
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  links: SitemapLink[];
}

const sitemap: SitemapSection[] = [
  {
    title: 'Main',
    description: 'Start here',
    icon: Home,
    links: [
      { label: 'Home', href: '/', icon: Home, desc: 'What Bloomneo runs, and why it is one subscription' },
      { label: 'About', href: '/about', icon: BookOpen, desc: 'Why we run the infrastructure nobody was hired to own' },
      { label: 'Contact', href: '/contact', icon: Mail, desc: 'Tell us what you are running' },
    ],
  },
  {
    title: 'Platform',
    description: 'What the subscription includes',
    icon: Server,
    links: [
      { label: 'Service level', href: '/sla', icon: Activity, desc: 'Uptime, response times, backups and service credits' },
    ],
  },
  {
    title: 'Use cases',
    description: 'The situations businesses come to us in',
    icon: Briefcase,
    links: [
      { label: 'All use cases', href: '/usecases', icon: Briefcase, desc: 'The five situations we see most' },
      { label: 'Your developer left', href: '/usecases/developer-left', icon: Briefcase, desc: 'Recovering access when the person who set it up has gone' },
      { label: 'Agency has gone quiet', href: '/usecases/agency-handover', icon: Building2, desc: 'Taking over what an agency built and stopped supporting' },
      { label: 'Too many providers', href: '/usecases/consolidate-providers', icon: ShoppingCart, desc: 'Consolidating scattered infrastructure onto one bill' },
      { label: 'Launching without ops', href: '/usecases/launching-without-ops', icon: Truck, desc: 'Getting infrastructure ready before launch day' },
      { label: 'After an outage', href: '/usecases/after-an-outage', icon: HeartPulse, desc: 'Fixing why nobody noticed, not just what broke' },
    ],
  },
  {
    title: 'Writing',
    description: 'Practical notes on running infrastructure',
    icon: Newspaper,
    links: [
      { label: 'Blog', href: '/blog', icon: Newspaper, desc: 'What it costs, what breaks, and who is responsible' },
    ],
  },
  {
    title: 'Company',
    description: 'Who we are and how to work with us',
    icon: Handshake,
    links: [
      { label: 'About', href: '/about', icon: BookOpen, desc: 'What we believe and how it shows up' },
      { label: 'Partners', href: '/partners', icon: Handshake, desc: 'For agencies, consultants and resellers' },
      { label: 'Contact', href: '/contact', icon: Mail, desc: 'Talk to us' },
    ],
  },
  {
    title: 'Account',
    description: 'Sign in or create an account',
    icon: LogIn,
    links: [
      { label: 'Sign in', href: '/auth/login', icon: LogIn, desc: 'Access your console' },
      { label: 'Create an account', href: '/auth/register', icon: UserPlus, desc: 'Start with Bloomneo' },
    ],
  },
  {
    title: 'Legal',
    description: 'Terms and policies',
    icon: FileText,
    links: [
      { label: 'Privacy Policy', href: '/privacy', icon: Shield, desc: 'What data we hold and why' },
      { label: 'Terms of Service', href: '/terms', icon: FileText, desc: 'The agreement between us' },
      { label: 'Cookie Policy', href: '/cookies', icon: Cookie, desc: 'What we set in your browser' },
      { label: 'Service Level Agreement', href: '/sla', icon: Activity, desc: 'What we commit to, and what happens if we miss it' },
      { label: 'Refund Policy', href: '/refund', icon: RotateCcw, desc: 'When money comes back' },
      { label: 'Cancellation', href: '/cancellation', icon: XCircle, desc: 'How to leave, and what happens to your data' },
    ],
  },
];

const TOTAL_PAGES = sitemap.reduce((acc, s) => acc + s.links.length, 0);

export default function SitemapPage() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Sitemap | Bloomneo"
        description="Complete sitemap of Bloomneo. Browse every platform, company and legal page in one place."
        keywords="bloomneo sitemap, site navigation, all pages, bloomneo links"
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden rounded-3xl border border-border bg-muted/20 px-4 py-12 text-center sm:px-6 md:py-16">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(var(--color-brand-mark) 1px, transparent 1px),' +
              'linear-gradient(90deg, var(--color-brand-mark) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse at center top, black 40%, transparent 75%)',
          }}
        />

        <div className="relative mx-auto max-w-3xl">
          <div className="bloom-rise inline-flex items-center gap-2.5 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
            <Map className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary">
              Sitemap
            </span>
          </div>

          <h1
            className="bloom-rise bloom-rise-1 font-bold tracking-[-0.04em] leading-[0.92] mb-6"
            style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}
          >
            Everything in<br />
            <span className="relative inline-block">
              <span className="relative z-10 text-primary">one place.</span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-primary/15 -z-0 -skew-x-6" />
            </span>
          </h1>

          <p className="bloom-rise bloom-rise-2 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Browse the full Bloomneo site — platform, company and legal pages, all in one place.
          </p>
        </div>
      </section>

      {/* ── Sections ─────────────────────────────────────────────────────── */}
      <div className="mt-16 space-y-8">
        {sitemap.map((section) => (
          <section
            key={section.title}
            className="bloom-rise bg-card border border-border rounded-3xl p-6 sm:p-8 md:p-10"
          >
            <div className="flex items-start gap-4 mb-6 pb-6 border-b border-border">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                <section.icon className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl md:text-2xl font-bold tracking-tight">{section.title}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{section.description}</p>
              </div>
              <span className="hidden sm:inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1">
                {section.links.length} {section.links.length === 1 ? 'page' : 'pages'}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.links.map((link) => {
                const Icon = link.icon;
                return (
                  <button
                    key={`${section.title}-${link.href}`}
                    onClick={() => navigate(link.href)}
                    className="group flex items-start gap-3 bg-muted/40 border border-border/60 rounded-2xl p-4 text-left hover:border-primary/30 hover:bg-muted/60 transition-all"
                  >
                    {Icon && (
                      <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground truncate">{link.label}</span>
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                      </div>
                      {link.desc && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{link.desc}</p>
                      )}
                      <div className="text-[10px] font-mono text-muted-foreground/60 mt-1.5 truncate">{link.href}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* ── At a glance ──────────────────────────────────────────────────── */}
      <section className="bloom-rise mt-8 bg-gradient-to-br from-primary/15 via-primary/5 to-primary/10 border border-primary/30 rounded-3xl p-8 md:p-10">
        <div className="text-center mb-8">
          <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-primary mb-2">
            At a glance
          </div>
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
            {TOTAL_PAGES} pages across {sitemap.length} sections
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {sitemap.slice(0, 4).map((s) => (
            <div key={s.title} className="bg-background/60 backdrop-blur border border-border/60 rounded-2xl p-4 text-center">
              <div className="text-2xl font-bold text-primary mb-1">{s.links.length}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{s.title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── XML sitemap ──────────────────────────────────────────────────── */}
      <section className="mt-16 mb-4 text-center">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-4">
          <FileText className="h-3.5 w-3.5" />
          For search engines
        </div>
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">XML Sitemap</h3>
        <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
          Crawlers and SEO tools can find our machine-readable sitemap at{' '}
          <a href="/sitemap.xml" className="text-primary underline underline-offset-4 font-mono text-sm">/sitemap.xml</a>
        </p>
        <button
          onClick={() => navigate('/contact')}
          className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-border bg-background hover:bg-muted/50 font-semibold text-sm text-foreground transition-all"
        >
          Can't find what you need? Contact us
          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </section>
    </>
  );
}
